// React Dependencies
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// MUI Imports
import { Grid } from "@mui/material";

import {
  ON_LOAD,
  CANCEL_PROGRAM,
  UPDATE_PROGRAM,
  PROCESS_ORDER,
  UPDATE_EMAILS,
  GET_MEMBERSHIPS,
  UPDATE_LOCKOUT,
  UPDATE_REPORT_DOWNLOADS,
  UPDATE_CALL_NOTES,
  UPDATE_USER_DETAILS
} from "../../../Helpers/trigger_constants.js";

import { logout } from "../../../Redux/Actions/userActions";

// Requester Imports
import {
  performUserDetailsRequest,
  performUserCallNotesRequest,
  performUserEmailLogsRequest,
  performUserMembershipsRequest,
  performUserTrackingRequest,
  performUserProgramsRequest,
  performUserPaymentRequest,
  performUserLoginHistoryRequest,
  performDispositionsRequest,
  performUserOrdersRequest,
  performUserMemberEventsRequest,
  performUserReportHistoryRequest,
  performUserCallLogRequest,
  performCustomerLockoutsRequest,
  performUserReportDownloadsRequest
} from "./CustomerRequesters/CustomerRequesters.js";

// Widget Imports
import CustomerDetailsWidget from "./CustomerDetailsWidget/CustomerDetailsWidget";
import MoreCustomerDetailsWidget from "./MoreCustomerDetailsWidget/MoreCustomerDetailsWidget";
import CustomerProgramsWidget from "./CustomerProgramsWidget/CustomerProgramsWidget";
import CustomerMembershipsWidget from "./CustomerMembershipsWidget/CustomerMembershipsWidget";
import CustomerPaymentDetailsWidget from "./CustomerPaymentDetailsWidget/CustomerPaymentDetailsWidget";
import CustomerTrackingDetailsWidget from "./CustomerTrackingDetailsWidget/CustomerTrackingDetailsWidget";
import CustomerOrdersWidget from "./CustomerOrdersWidget/CustomerOrdersWidget";
import CustomerEmailLogWidget from "./CustomerEmailLogWidget/CustomerEmailLogWidget";
import CustomerCallNotesWidget from "./CustomerCallNotesWidget/CustomerCallNotesWidget";
import CustomerLoginHistoryWidget from "./CustomerLoginHistoryWidget/CustomerLoginHistoryWidget";
import MemberEventsWidget from "./MemberEventsWidget/MemberEventsWidget";
import CustomerLockoutsWidget from "./CustomerLockoutsWidget/CustomerLockoutsWidget";
import CustomerReportDownloadsWidget from "./CustomerReportDownloadsWidget/CustomerReportDownloadsWidget";
import CustomerReportHistoryWidget from "./CustomerReportHistoryWidget/CustomerReportHistoryWidget.js";
import CustomerFive9Widget from "./CustomerFive9Widget/CustomerFive9Widget.js";

// Component Imports
import SuccessAlert from "../../../Components/Interactive/SuccessAlert";
import FailAlert from "../../../Components/Interactive/FailAlert";

// CSS Imports
import "./CustomerStyles.css";

import ScrollToTop from "../../../Components/Interactive/ScrollToTop.js";

const Customer = ({ isOpen }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userAuth = useSelector((state) => state.auth);
  const isAdminOrSuperUser = userAuth?.userInfo?.userData?.role === "Administrator" || userAuth?.userInfo?.userData?.role === "Super User" || userAuth?.userInfo?.userData?.role === "Supervisor";
  const userId = userAuth?.userInfo?.userData?.userId;
  useEffect(() => {
    (async () => {
      const res = await fetch(`/api/auth/get-auth`, {
        method: "GET",
        credentials: "include",
      });
      if (res.status === 401) {
        dispatch(logout());
        return navigate("/");
      }
    })();
  }, [dispatch, navigate]);

  // Basic feedback state that is top level and can be used anywhere
  const [successPopupIsOpen, setSuccessPopupIsOpen] = useState(false);
  const [failPopupIsOpen, setFailPopupIsOpen] = useState(false);

  /* Impersonation Redux State (Prop Drilling) */
  const [loading, setIsLoading] = useState(false);
  const [trigger, setTrigger] = useState(ON_LOAD);
  
  const [customerData, setCustomerData] = useState({
    userDetailsInfo: { success: false },
    userOrders: { orders: [], success: false },
    userPaymentInfo: { success: false },
    userCallNotesInfo: { callNotes: [], success: false },
    userEmailNotesInfo: { emailNotes: [], success: false },
    userMembershipInfo: { membershipInfo: [], success: false },
    userTrackingInfo: { success: false },
    userProgramsInfo: { programInfo: [], success: false },
    userLoginHistoryInfo: { loginHistoryInfo: [], success: false },
    dispositions: { dispositions: [], success: false },
    userEvents: { events: [], success: false },
    userReportHistory: { history: {}, success: false },
    userCallLog: { callLog: [], success: false },
    userLockouts: { lockouts: [], success: false },
    userReportDownloads: { reportDownloads: {}, success: false }
  });

  const [updatedData, setUpdatedData] = useState({
    updateData: { state: "", message: "" },
  });
  /**********************************/

  useEffect(() => {
    switch (trigger) {
      case ON_LOAD:
        performUserDetailsRequest(
          id,
          userId,
          setIsLoading,
          setCustomerData,
          navigate
        );
        performUserOrdersRequest(id, setIsLoading, setCustomerData, navigate);
        performUserCallNotesRequest(id, setIsLoading, setCustomerData, navigate);
        performDispositionsRequest(id, setIsLoading, setCustomerData, navigate);
        performUserEmailLogsRequest(id, setIsLoading, setCustomerData, navigate);
        performUserMembershipsRequest(
          id,
          setIsLoading,
          setCustomerData,
          navigate
        );
        performUserTrackingRequest(id, setIsLoading, setCustomerData, navigate);
        performUserProgramsRequest(id, setIsLoading, setCustomerData, navigate);
        performUserPaymentRequest(id, setIsLoading, setCustomerData, navigate);
        performUserLoginHistoryRequest(
          id,
          setIsLoading,
          setCustomerData,
          navigate
        );
        performUserMemberEventsRequest(
          id,
          setIsLoading,
          setCustomerData,
          navigate
        );
        performUserReportHistoryRequest(
          id,
          setIsLoading,
          setCustomerData,
          navigate
        );
        performUserCallLogRequest(id, setIsLoading, setCustomerData, navigate);
        performCustomerLockoutsRequest(id,setIsLoading,setCustomerData,navigate);
        performUserReportDownloadsRequest(id,setIsLoading,setCustomerData,navigate);
        break;
      case UPDATE_PROGRAM:
        performUserOrdersRequest(id, setIsLoading, setCustomerData, navigate);
        performUserCallNotesRequest(id, setIsLoading, setCustomerData, navigate);
        performUserEmailLogsRequest(id, setIsLoading, setCustomerData, navigate);
        performUserMemberEventsRequest(
          id,
          setIsLoading,
          setCustomerData,
          navigate
        );
        performUserProgramsRequest(id, setIsLoading, setCustomerData, navigate);
        break;
      case UPDATE_EMAILS:
        performUserEmailLogsRequest(id, setIsLoading, setCustomerData, navigate);
        break;
      case CANCEL_PROGRAM:
        performUserCallNotesRequest(id, setIsLoading, setCustomerData, navigate);
        performUserEmailLogsRequest(id, setIsLoading, setCustomerData, navigate);
        performUserMemberEventsRequest(
          id,
          setIsLoading,
          setCustomerData,
          navigate
        );
        break;
      case PROCESS_ORDER:
        performUserOrdersRequest(id, setIsLoading, setCustomerData, navigate);
        performUserCallNotesRequest(id, setIsLoading, setCustomerData, navigate);
        performUserEmailLogsRequest(id, setIsLoading, setCustomerData, navigate);
        break;
      case GET_MEMBERSHIPS:
        performUserMembershipsRequest(
          id,
          setIsLoading,
          setCustomerData,
          navigate
        );
        performUserCallNotesRequest(id, setIsLoading, setCustomerData, navigate);
        break;
      case UPDATE_LOCKOUT:
        performCustomerLockoutsRequest(id, setIsLoading, setCustomerData, navigate);
        break;
      case UPDATE_REPORT_DOWNLOADS:
        performUserReportDownloadsRequest(id,setIsLoading,setCustomerData,navigate);
        break;
      case UPDATE_CALL_NOTES:
        performUserCallNotesRequest(id, setIsLoading, setCustomerData, navigate);
        break;
      case UPDATE_USER_DETAILS:
        performUserDetailsRequest(id, userId, setIsLoading, setCustomerData, navigate);
        break;
      default:
        return;
    }
  }, [trigger, navigate, id, userId]);

  // Checks app-wide edit state for popups
  useEffect(() => {
    if (updatedData.updateData?.state === true) successPopup();
    if (updatedData.updateData?.state === false) failPopup();
  }, [updatedData]);

  const successPopup = () => {
    setSuccessPopupIsOpen(true);
    setTimeout(() => setSuccessPopupIsOpen(false), 7500);
  };
  const failPopup = () => {
    setFailPopupIsOpen(true);
    setTimeout(() => setFailPopupIsOpen(false), 7500);
  };
  return (
    <div className={`${isOpen ? "page" : "page-closed"}`}>
      <Grid container spacing={2}>
        <Grid size={{ xs:12, sm:12, md:12, lg:10, xl:10 }}>
          {successPopupIsOpen && (
            <SuccessAlert className="alert-animation">
              {updatedData.updateData.message}
            </SuccessAlert>
          )}
          {failPopupIsOpen && (
            <FailAlert>{updatedData.updateData.message}</FailAlert>
          )}
          <CustomerDetailsWidget
            id={id}
            allCustomerData={customerData}
            loading={loading}
            setCustomerData={setCustomerData}
            setUpdatedData={setUpdatedData}
            setTrigger={setTrigger}
          />
        </Grid>
        <Grid size={{ xs:12, sm:12, md:12, lg:2, xl:2 }}>
          <MoreCustomerDetailsWidget
            id={id}
            allCustomerData={customerData}
            setCustomerData={setCustomerData}
            setUpdatedData={setUpdatedData}
            setTrigger={setTrigger}
            loading={loading}
          />
        </Grid>
        <Grid size={{md:12, lg:10, xl:10 }}>
          <CustomerOrdersWidget
            id={id}
            allCustomerData={customerData}
            setUpdatedData={setUpdatedData}
            setTrigger={setTrigger}
            loading={loading}
          />
          <Grid size={{ lg:12, xl:12}}>
            <CustomerCallNotesWidget
              id={id}
              allCustomerData={customerData}
              setCustomerData={setCustomerData}
              setUpdatedData={setUpdatedData}
              setTrigger={setTrigger}
              loading={loading}
            />
            <Grid size={{ lg:12, xl:12}}>
              <CustomerEmailLogWidget
                id={id}
                allCustomerData={customerData}
                setCustomerData={setCustomerData}
                setUpdatedData={setUpdatedData}
                setTrigger={setTrigger}
                loading={loading}
              />
            </Grid>
            <Grid size={{ lg:12, xl:12 }}>
              <CustomerLoginHistoryWidget
                id={id}
                allCustomerData={customerData}
                loading={loading}
                setCustomerData={setCustomerData}
                setUpdatedData={setUpdatedData}
                setTrigger={setTrigger}
              />
            </Grid>
            <Grid size={{ lg:12, xl:12 }}>
              <CustomerLockoutsWidget
                id={id}
                allCustomerData={customerData}
                loading={loading}
                setCustomerData={setCustomerData}
                setUpdatedData={setUpdatedData}
                setTrigger={setTrigger}
              />
            </Grid>
            <Grid size={{ lg:12, xl:12 }}>
              <CustomerReportDownloadsWidget
                id={id}
                userId={userId}
                allCustomerData={customerData}
                loading={loading}
                setCustomerData={setCustomerData}
                setUpdatedData={setUpdatedData}
                setTrigger={setTrigger}
              />
            </Grid>
            {isAdminOrSuperUser && (
              <Grid size={{ lg: 12, xl:12 }}>
                <MemberEventsWidget
                  id={id}
                  allCustomerData={customerData}
                  loading={loading}
                  setCustomerData={setCustomerData}
                  setUpdatedData={setUpdatedData}
                  setTrigger={setTrigger}
                />
              </Grid>
            )}
            {isAdminOrSuperUser && (
              <Grid size={{ lg:12, xl:12 }}>
                <CustomerReportHistoryWidget
                  id={id}
                  allCustomerData={customerData}
                  loading={loading}
                  setCustomerData={setCustomerData}
                  setUpdatedData={setUpdatedData}
                  setTrigger={setTrigger}
                />
              </Grid>
            )}
            {isAdminOrSuperUser && (
              <Grid size={{ lg:12, xl:12 }}>
                <CustomerFive9Widget
                  id={id}
                  allCustomerData={customerData}
                  loading={loading}
                  setCustomerData={setCustomerData}
                  setUpdatedData={setUpdatedData}
                  setTrigger={setTrigger}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid size={{ xs:12, md:12, lg:2, xl:2 }}>
          <CustomerPaymentDetailsWidget
            id={id}
            allCustomerData={customerData}
            loading={loading}
            setCustomerData={setCustomerData}
            setUpdatedData={setUpdatedData}
            setTrigger={setTrigger}
          />
          <Grid size={{ xs:12, md:12, lg:12, xl:12 }}>
            <CustomerProgramsWidget
              id={id}
              allCustomerData={customerData}
              setCustomerData={setCustomerData}
              setUpdatedData={setUpdatedData}
              setTrigger={setTrigger}
              loading={loading}
            />
          </Grid>
          <Grid size={{ lg:12, xl:12 }}>
            <CustomerMembershipsWidget
              id={id}
              allCustomerData={customerData}
              setCustomerData={setCustomerData}
              setUpdatedData={setUpdatedData}
              setTrigger={setTrigger}
              loading={loading}
            />
          </Grid>
          <Grid size={{ lg:12, xl:12 }}>
            <CustomerTrackingDetailsWidget
              loading={loading}
              id={id}
              allCustomerData={customerData}
              setCustomerData={setCustomerData}
              setUpdatedData={setUpdatedData}
              setTrigger={setTrigger}
            />
          </Grid>
        </Grid>
      </Grid>
      <ScrollToTop />
    </div>
  );
};

export default Customer;
